import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Link2, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useUpdateDeal, useVerificationLinkStatus, verificationLinkQueryKey } from '@/hooks/use-deals';
import type {
  Deal,
  UpdateDealInput,
  VerificationLinkBlocker,
  VerificationLinkBlockerTask,
  VerificationLinkDetail,
  VerificationLinkStatus,
} from '@/hooks/use-deals';
import { useDeleteTask, useUpdateTask } from '@/hooks/use-tasks';
import { cn } from '@/lib/utils';

function StatusDot({ active, className }: { active: boolean | null; className?: string }) {
  return (
    <span
      className={cn(
        'inline-block size-2 shrink-0 rounded-full',
        active === null && 'bg-muted-foreground/40',
        active === true && 'bg-emerald-500',
        active === false && 'bg-red-500',
        className,
      )}
    />
  );
}

function LinkRow({ label, link }: { label: string; link: VerificationLinkDetail }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      toast.success(`Copied ${label.toLowerCase()} link`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className="flex items-center gap-3">
      <StatusDot active={link.active} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium">{label}</span>
          <span className={cn('text-[11px]', link.active ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
            {link.active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className="truncate font-mono text-[11px] text-muted-foreground" title={link.url}>
          {link.url}
        </div>
      </div>
      <Button variant="outline" size="sm" className="h-7 shrink-0 px-2" onClick={handleCopy}>
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        <span className="ml-1 text-xs">Copy</span>
      </Button>
    </div>
  );
}

function SubmissionTaskRow({
  task,
  onChanged,
}: {
  task: VerificationLinkBlockerTask;
  onChanged: () => void;
}) {
  const { mutate: updateTask, isPending: isRetagging } = useUpdateTask();
  const { mutate: deleteTask, isPending: isDeleting } = useDeleteTask();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const busy = isRetagging || isDeleting;

  // Strip the tag so the row shows the human-readable part of the task
  const summary = task.task.replace(/^\[Contact Verification\]\s*/, '').split('\n')[0];

  const handleRetag = () => {
    updateTask(
      { id: task.id, task: task.superseded_task },
      {
        onSuccess: () => {
          toast.success('Task kept as a record; it no longer counts as a submission');
          onChanged();
        },
        onError: (error: Error) => toast.error(error.message),
      },
    );
  };

  const handleDelete = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    deleteTask(task.id, {
      onSuccess: () => {
        toast.success('Task deleted');
        onChanged();
      },
      onError: (error: Error) => toast.error(error.message),
    });
  };

  return (
    <div className="rounded-md border bg-muted/40 p-2">
      <div className="truncate text-[11px] text-muted-foreground" title={task.task}>
        {summary}
      </div>
      <div className="mt-1.5 flex items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          className="h-6 px-2 text-[11px]"
          onClick={handleRetag}
          disabled={busy}
          title={`Change the tag to "[Contact Verification - Superseded]" so the task stays but no longer counts as a submission`}
        >
          {isRetagging && <Loader2 className="mr-1 size-3 animate-spin" />}
          Keep as record
        </Button>
        <Button
          variant={confirmingDelete ? 'destructive' : 'outline'}
          size="sm"
          className="h-6 px-2 text-[11px]"
          onClick={handleDelete}
          onBlur={() => setConfirmingDelete(false)}
          disabled={busy}
        >
          {isDeleting ? <Loader2 className="mr-1 size-3 animate-spin" /> : <Trash2 className="mr-1 size-3" />}
          {confirmingDelete ? 'Confirm delete' : 'Delete task'}
        </Button>
      </div>
    </div>
  );
}

function BlockerItem({
  blocker,
  deal,
  status,
  onTasksChanged,
}: {
  blocker: VerificationLinkBlocker;
  deal: Deal;
  status: VerificationLinkStatus;
  onTasksChanged: () => void;
}) {
  const { mutate: updateDeal, isPending } = useUpdateDeal();

  const applyDealChange = (input: UpdateDealInput, successMessage: string) => {
    updateDeal(
      { id: deal.id, ...input },
      {
        onSuccess: () => toast.success(successMessage),
        onError: (error: unknown) => toast.error(error instanceof Error ? error.message : 'Failed to update deal'),
      },
    );
  };

  const control = (() => {
    switch (blocker.code) {
      case 'outcome_set': {
        return (
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-[11px]"
            disabled={isPending}
            onClick={() => applyDealChange({ outcome: null }, 'Outcome cleared')}
          >
            {isPending && <Loader2 className="mr-1 size-3 animate-spin" />}
            Clear outcome
          </Button>
        );
      }
      case 'archived': {
        return (
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-[11px]"
            disabled={isPending}
            onClick={() => applyDealChange({ archived_at: null }, 'Deal unarchived')}
          >
            {isPending && <Loader2 className="mr-1 size-3 animate-spin" />}
            Unarchive deal
          </Button>
        );
      }
      case 'completed_stage':
      case 'stage_requires_submission': {
        if (status.acceptable_stages.length === 0) {
          return <p className="text-[11px] text-muted-foreground">No stage is acceptable until a submission exists.</p>;
        }
        return (
          <Select
            value=""
            disabled={isPending}
            onValueChange={(value) => applyDealChange({ deal_stage: value }, `Stage moved to ${value}`)}
          >
            <SelectTrigger className="h-7 w-full text-xs">
              <SelectValue placeholder={isPending ? 'Updating stage...' : 'Move to an acceptable stage...'} />
            </SelectTrigger>
            <SelectContent>
              {status.acceptable_stages.map((stage) => (
                <SelectItem key={stage} value={stage} className="text-xs">
                  {stage}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
      case 'verification_contact_submitted':
      case 'registrant_submitted': {
        return (
          <div className="space-y-1.5">
            {blocker.tasks?.map((task) => (
              <SubmissionTaskRow key={task.id} task={task} onChanged={onTasksChanged} />
            ))}
          </div>
        );
      }
      case 'deal_type_not_allowed': {
        return <p className="text-[11px] text-muted-foreground">Change the deal type in the header to enable the link.</p>;
      }
      default: {
        return null;
      }
    }
  })();

  return (
    <li className="space-y-1.5">
      <div className="flex items-start gap-2">
        <StatusDot active={false} className="mt-1.5" />
        <div className="flex-1 text-xs leading-snug">
          {blocker.message}
          {blocker.affects === 'registrant' && (
            <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground">
              registrant link only
            </span>
          )}
        </div>
      </div>
      <div className="pl-4">{control}</div>
    </li>
  );
}

/**
 * Header control for the public verification link: per-link active status with
 * copy buttons, and a live checklist of every condition currently blocking a
 * link, each with the control that resolves it. Actions apply immediately and
 * the checklist refreshes from the server.
 */
export function VerificationLinkControl({ deal }: { deal: Deal }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data: status, isLoading, isError, refetch, isFetching } = useVerificationLinkStatus(deal.id);

  const invalidateStatus = () => {
    void queryClient.invalidateQueries({ queryKey: verificationLinkQueryKey(deal.id) });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen)
      void refetch();
  };

  const activeCount = status
    ? Number(status.links.registrant.active) + Number(status.links.verification.active)
    : null;

  const dotClass = (() => {
    if (activeCount === null)
      return 'bg-muted-foreground/40';
    if (activeCount === 2)
      return 'bg-emerald-500';
    if (activeCount === 1)
      return 'bg-amber-500';
    return 'bg-red-500';
  })();

  const summaryBadge = (() => {
    if (activeCount === null)
      return null;
    if (activeCount === 2)
      return <Badge className="bg-emerald-600 hover:bg-emerald-600">Both active</Badge>;
    if (activeCount === 1)
      return <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300">Partially active</Badge>;
    return <Badge variant="secondary" className="bg-red-100 text-red-900 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300">Inactive</Badge>;
  })();

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-md px-3 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Public verification link"
        >
          <span className={cn('size-2 rounded-full', dotClass)} />
          <Link2 className="size-4" />
          <span>Verification link</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[440px] p-0">
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Verification link</span>
            {summaryBadge}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="size-7 p-0 text-muted-foreground"
            onClick={() => void refetch()}
            disabled={isFetching}
            title="Refresh status"
          >
            <RefreshCw className={cn('size-3.5', isFetching && 'animate-spin')} />
          </Button>
        </div>
        <Separator />

        {isLoading && (
          <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Checking link status...
          </div>
        )}

        {isError && !isLoading && (
          <div className="p-4 text-sm text-destructive">
            Couldn't load the link status.
            {' '}
            <button type="button" className="underline" onClick={() => void refetch()}>
              Try again
            </button>
          </div>
        )}

        {status && (
          <>
            <div className="space-y-3 px-4 py-3">
              <LinkRow label="Registrant" link={status.links.registrant} />
              <LinkRow label="Verification contact" link={status.links.verification} />
            </div>

            {status.blockers.length > 0 && (
              <>
                <Separator />
                <div className="px-4 py-3">
                  <div className="mb-2 text-xs font-medium">Blocking conditions</div>
                  <ul className="space-y-3">
                    {status.blockers.map((blocker) => (
                      <BlockerItem
                        key={blocker.code}
                        blocker={blocker}
                        deal={deal}
                        status={status}
                        onTasksChanged={invalidateStatus}
                      />
                    ))}
                  </ul>
                </div>
              </>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
